import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Button,
  Badge,
  Input,
  Select,
  SimpleGrid,
  Progress,
  Alert,
  AlertIcon,
  AlertDescription,
  Tabs,
  TabList,
  Tab,
  useDisclosure,
} from "@chakra-ui/react";
import { useQuery } from "react-query";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import Card from "../components/Card/Card";
import CardHeader from "../components/Card/CardHeader";
import CardBody from "../components/Card/CardBody";
import CustomModal from "../components/Modal";
import {
  previewWarehouseStock,
  applyWarehouseStock,
  getWarehouseImports,
  getWarehouseImport,
} from "../services/warehouseStockService";

const PAGE_SIZE = 50;
const POLL_MS = 2000;

const TABS = [
  { key: "changes", label: "تغییرات" },
  { key: "notInSystem", label: "در فایل هست، در سیستم نیست" },
  { key: "skipped", label: "خالی یا منفی (اعمال نمی‌شود)" },
  { key: "systemNotInFile", label: "در سیستم هست، در فایل نیست" },
];

const IMPORT_STATUS = {
  running: { label: "در حال اجرا", color: "blue" },
  completed: { label: "انجام شد", color: "green" },
  failed: { label: "خطا", color: "red" },
};

/** یکسان‌سازی ی/ک عربی و حذف فاصله‌ها برای تشخیص عنوان ستون‌ها */
function normalizeHeader(value) {
  return String(value ?? "")
    .replace(/ي/g, "ی")
    .replace(/ك/g, "ک")
    .replace(/\s+/g, "");
}

/**
 * خواندن فایل انبار: ستون‌های «کد کالا»، «نام کالا» و «موجودی» از روی عنوان پیدا می‌شوند؛
 * اگر عنوانی پیدا نشد، ستون اول کد، دوم نام و سوم موجودی فرض می‌شود.
 */
function parseWarehouseSheet(arrayBuffer) {
  const wb = XLSX.read(arrayBuffer, { type: "array" });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  if (!sheet) throw new Error("شیتی در فایل پیدا نشد");
  const table = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: null, blankrows: false });

  let headerIdx = -1;
  let cols = { code: 0, name: 1, stock: 2 };
  for (let i = 0; i < Math.min(table.length, 10); i++) {
    const cells = (table[i] || []).map(normalizeHeader);
    const code = cells.findIndex((c) => c.includes("کد"));
    const stock = cells.findIndex((c) => c.includes("موجود") || c.includes("تعداد"));
    if (code >= 0 && stock >= 0) {
      const name = cells.findIndex((c) => c.includes("نام") || c.includes("شرح"));
      headerIdx = i;
      cols = { code, name, stock };
      break;
    }
  }

  const rows = [];
  for (const r of table.slice(headerIdx + 1)) {
    const code = r?.[cols.code];
    if (code == null || String(code).trim() === "") continue;
    rows.push({
      code: String(code).trim(),
      name: cols.name >= 0 ? r[cols.name] ?? "" : "",
      stock: r[cols.stock] ?? null,
    });
  }
  return { rows, headerFound: headerIdx >= 0, sheetName: wb.SheetNames[0] };
}

function formatDate(value) {
  if (!value) return "-";
  return new Intl.DateTimeFormat("fa-IR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function StatBox({ label, value, color }) {
  const bg = useColorModeValue("gray.50", "whiteAlpha.100");
  return (
    <Box bg={bg} borderRadius="lg" p={3}>
      <Text fontSize="xs" color="gray.500">{label}</Text>
      <Text fontSize="xl" fontWeight="bold" color={color}>
        {Number(value || 0).toLocaleString("fa-IR")}
      </Text>
    </Box>
  );
}

function WarehouseStock() {
  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const fileInputRef = useRef(null);
  const confirm = useDisclosure();

  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState(null);
  const [headerFound, setHeaderFound] = useState(true);
  const [diff, setDiff] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [tab, setTab] = useState(0);
  const [direction, setDirection] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [isApplying, setIsApplying] = useState(false);
  const [activeImportId, setActiveImportId] = useState(null);
  const [activeImport, setActiveImport] = useState(null);

  const { data: importsData, refetch: refetchImports } = useQuery(
    ["warehouse-stock-imports"],
    getWarehouseImports,
    { staleTime: 10 * 1000 }
  );
  const imports = importsData?.data || [];

  useEffect(() => {
    if (!activeImportId) return undefined;
    let stopped = false;
    const poll = async () => {
      try {
        const res = await getWarehouseImport(activeImportId);
        if (stopped) return;
        setActiveImport(res.data);
        if (res.data?.status === "running") {
          setTimeout(poll, POLL_MS);
          return;
        }
        refetchImports();
        const s = res.data?.summary || {};
        if (res.data?.status === "completed") {
          toast.success(
            `موجودی ${s.applied} تنوع به‌روز شد${s.failed ? ` (${s.failed} ناموفق)` : ""}`
          );
        } else {
          toast.error(res.data?.error || "اعمال موجودی با خطا متوقف شد");
        }
        setDiff(null);
        setRows(null);
      } catch (err) {
        if (!stopped) setTimeout(poll, POLL_MS * 2);
      }
    };
    poll();
    return () => {
      stopped = true;
    };
  }, [activeImportId, refetchImports]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setFileName(file.name);
    setDiff(null);
    setRows(null);
    setActiveImport(null);
    setActiveImportId(null);
    try {
      const parsed = parseWarehouseSheet(await file.arrayBuffer());
      if (parsed.rows.length === 0) {
        toast.error("ردیفی با کد کالا در فایل پیدا نشد");
        return;
      }
      setRows(parsed.rows);
      setHeaderFound(parsed.headerFound);
      setIsChecking(true);
      const res = await previewWarehouseStock(parsed.rows);
      setDiff(res.data);
      setTab(0);
      setPage(1);
      setSearch("");
      setDirection("");
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "خطا در خواندن فایل");
    } finally {
      setIsChecking(false);
    }
  };

  const handleApply = async () => {
    if (!rows || !diff) return;
    setIsApplying(true);
    try {
      const res = await applyWarehouseStock({
        rows,
        fileName,
        codes: diff.changes.map((c) => c.code),
      });
      confirm.onClose();
      setActiveImport(null);
      setActiveImportId(res.data?.importId);
    } catch (err) {
      toast.error(err.response?.data?.message || "خطا در شروع اعمال موجودی");
    } finally {
      setIsApplying(false);
    }
  };

  const tabKey = TABS[tab].key;
  const list = useMemo(() => {
    if (!diff) return [];
    let items =
      tabKey === "skipped" ? [...diff.skippedEmpty, ...diff.skippedNegative] : diff[tabKey] || [];
    if (tabKey === "changes" && direction) {
      items = items.filter((c) => (direction === "up" ? c.to > c.current : c.to < c.current));
    }
    const q = search.trim().toLowerCase();
    if (q) {
      items = items.filter((c) =>
        [c.code, c.name, c.fileName, c.productName, c.variantName]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    }
    return items;
  }, [diff, tabKey, direction, search]);

  const pages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const pageItems = list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const downloadReport = () => {
    if (!diff) return;
    const wb = XLSX.utils.book_new();
    const add = (name, items, map) =>
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(items.map(map)), name);
    const sys = (c) => ({
      "کد": c.code,
      "محصول": c.productName,
      "تنوع": c.variantName,
      "موجودی سیستم": c.current,
    });
    add("تغییرات", diff.changes, (c) => ({ ...sys(c), "موجودی فایل": c.to, "تفاوت": c.to - c.current }));
    add("نیست در سیستم", diff.notInSystem, (c) => ({ "کد": c.code, "نام در فایل": c.name, "موجودی فایل": c.stock }));
    add("خالی یا منفی", [...diff.skippedEmpty, ...diff.skippedNegative], (c) => ({ ...sys(c), "موجودی فایل": c.fileStock }));
    add("نیست در فایل", diff.systemNotInFile, sys);
    XLSX.writeFile(wb, `مغایرت-موجودی-${Date.now()}.xlsx`);
  };

  const running = activeImport?.status === "running" || (activeImportId && !activeImport);
  const progress = activeImport?.summary?.changes
    ? Math.round(
        ((activeImport.summary.applied + activeImport.summary.failed) / activeImport.summary.changes) * 100
      )
    : 0;

  const renderRow = (c, i) => {
    if (tabKey === "notInSystem") {
      return (
        <Tr key={`${c.code}-${i}`}>
          <Td borderColor={borderColor}>{c.code}</Td>
          <Td borderColor={borderColor} colSpan={2}>{c.name || "—"}</Td>
          <Td borderColor={borderColor}>—</Td>
          <Td borderColor={borderColor}>{c.stock ?? "خالی"}</Td>
        </Tr>
      );
    }
    const fileValue = tabKey === "changes" ? c.to : tabKey === "skipped" ? c.fileStock ?? "خالی" : "—";
    const delta = tabKey === "changes" ? c.to - c.current : null;
    return (
      <Tr key={`${c.logicalVariantId}-${i}`}>
        <Td borderColor={borderColor}>{c.code}</Td>
        <Td borderColor={borderColor}>
          <Text fontSize="sm">{c.productName}</Text>
          {!c.isActive && <Badge colorScheme="gray" fontSize="2xs">غیرفعال</Badge>}
        </Td>
        <Td borderColor={borderColor}><Text fontSize="sm">{c.variantName || "—"}</Text></Td>
        <Td borderColor={borderColor}>{c.current}</Td>
        <Td borderColor={borderColor}>
          {fileValue}
          {delta != null && (
            <Badge ms={2} colorScheme={delta > 0 ? "green" : "red"} dir="ltr">
              {delta > 0 ? `+${delta}` : delta}
            </Badge>
          )}
        </Td>
      </Tr>
    );
  };

  return (
    <>
      <Flex direction="column" dir="rtl">
        <Box mx="auto" w="100%" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Card mb={6}>
            <CardHeader>
              <Flex direction="column">
                <Text fontSize="lg" color={textColor} fontWeight="bold" me="auto">
                  به‌روزرسانی موجودی از فایل انبار
                </Text>
                <Text fontSize="md" fontWeight="400" me="auto" color="gray.400">
                  فایل اکسل را آپلود کنید تا با موجودی سیستم مقایسه شود. تا وقتی «اعمال» را نزنید چیزی تغییر نمی‌کند.
                </Text>
              </Flex>
            </CardHeader>
            <CardBody>
              <Flex direction="column" w="100%" gap={4}>
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription fontSize="sm">
                    فایل باید ستون‌های «کد کالا»، «نام کالا» و «موجودی» را داشته باشد. کد کالا با کد حسابداری تنوع‌ها
                    تطبیق داده می‌شود. موجودی خالی یا منفی اعمال نمی‌شود و بعد از اعمال، موجودی جدید به همهٔ درگاه‌ها
                    ارسال می‌شود.
                  </AlertDescription>
                </Alert>

                <Flex gap={3} align="center" flexWrap="wrap">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    style={{ display: "none" }}
                    onChange={handleFile}
                  />
                  <Button
                    colorScheme="teal"
                    onClick={() => fileInputRef.current?.click()}
                    isLoading={isChecking}
                    loadingText="در حال بررسی..."
                    isDisabled={running}
                  >
                    انتخاب فایل اکسل
                  </Button>
                  {fileName && <Text fontSize="sm" color="gray.500">{fileName}</Text>}
                  {rows && <Text fontSize="sm" color="gray.500">— {rows.length.toLocaleString("fa-IR")} ردیف</Text>}
                </Flex>

                {rows && !headerFound && (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription fontSize="sm">
                      عنوان ستون‌ها پیدا نشد؛ ستون اول کد، دوم نام و سوم موجودی در نظر گرفته شد. نتیجه را قبل از اعمال بررسی کنید.
                    </AlertDescription>
                  </Alert>
                )}

                {activeImportId && (
                  <Box>
                    <Text fontSize="sm" mb={2}>
                      {running
                        ? "در حال اعمال موجودی..."
                        : IMPORT_STATUS[activeImport?.status]?.label}{" "}
                      {activeImport?.summary &&
                        `${(activeImport.summary.applied + activeImport.summary.failed).toLocaleString("fa-IR")} از ${activeImport.summary.changes.toLocaleString("fa-IR")}`}
                    </Text>
                    <Progress
                      value={running ? progress : 100}
                      isIndeterminate={running && !activeImport}
                      colorScheme={activeImport?.status === "failed" ? "red" : "teal"}
                      borderRadius="md"
                    />
                  </Box>
                )}

                {diff && (
                  <>
                    <SimpleGrid columns={{ base: 2, md: 4, lg: 7 }} spacing={3}>
                      <StatBox label="کدهای فایل" value={diff.stats.fileRows} />
                      <StatBox label="تطبیق با سیستم" value={diff.stats.matched} />
                      <StatBox label="بدون تغییر" value={diff.stats.unchanged} />
                      <StatBox label="افزایش" value={diff.stats.increase} color="green.500" />
                      <StatBox label="کاهش" value={diff.stats.decrease} color="red.500" />
                      <StatBox label="خالی/منفی" value={diff.stats.skippedEmpty + diff.stats.skippedNegative} color="orange.400" />
                      <StatBox label="نیست در سیستم" value={diff.stats.notInSystem} color="gray.500" />
                    </SimpleGrid>

                    {diff.stats.duplicates > 0 && (
                      <Alert status="warning" borderRadius="md">
                        <AlertIcon />
                        <AlertDescription fontSize="sm">
                          {diff.stats.duplicates.toLocaleString("fa-IR")} کد تکراری در فایل هست؛ برای هر کد آخرین ردیف در نظر گرفته شد.
                        </AlertDescription>
                      </Alert>
                    )}

                    <Flex gap={3} flexWrap="wrap">
                      <Button
                        colorScheme="teal"
                        onClick={confirm.onOpen}
                        isDisabled={diff.changes.length === 0 || running}
                      >
                        اعمال {diff.changes.length.toLocaleString("fa-IR")} تغییر
                      </Button>
                      <Button variant="outline" onClick={downloadReport}>
                        دانلود گزارش اکسل
                      </Button>
                    </Flex>

                    <Tabs
                      index={tab}
                      onChange={(i) => {
                        setTab(i);
                        setPage(1);
                      }}
                      variant="soft-rounded"
                      colorScheme="teal"
                      size="sm"
                    >
                      <TabList flexWrap="wrap" gap={2}>
                        {TABS.map((t) => {
                          const count =
                            t.key === "skipped"
                              ? diff.skippedEmpty.length + diff.skippedNegative.length
                              : diff[t.key].length;
                          return (
                            <Tab key={t.key}>
                              {t.label} ({count.toLocaleString("fa-IR")})
                            </Tab>
                          );
                        })}
                      </TabList>
                    </Tabs>

                    <Flex gap={3} flexWrap="wrap">
                      <Input
                        maxW="300px"
                        placeholder="جستجو در کد، نام محصول یا تنوع"
                        value={search}
                        onChange={(e) => {
                          setSearch(e.target.value);
                          setPage(1);
                        }}
                      />
                      {tabKey === "changes" && (
                        <Select
                          maxW="180px"
                          value={direction}
                          onChange={(e) => {
                            setDirection(e.target.value);
                            setPage(1);
                          }}
                        >
                          <option value="">افزایش و کاهش</option>
                          <option value="up">فقط افزایش</option>
                          <option value="down">فقط کاهش</option>
                        </Select>
                      )}
                    </Flex>

                    <Box overflowX="auto">
                      <Table variant="simple" size="sm" color={textColor}>
                        <Thead>
                          <Tr>
                            <Th borderColor={borderColor}>کد</Th>
                            <Th borderColor={borderColor}>محصول</Th>
                            <Th borderColor={borderColor}>تنوع</Th>
                            <Th borderColor={borderColor}>موجودی سیستم</Th>
                            <Th borderColor={borderColor}>موجودی فایل</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {pageItems.length === 0 ? (
                            <Tr>
                              <Td colSpan={5} textAlign="center" py={6}>
                                <Text color="gray.400">موردی نیست</Text>
                              </Td>
                            </Tr>
                          ) : (
                            pageItems.map(renderRow)
                          )}
                        </Tbody>
                      </Table>
                    </Box>

                    {pages > 1 && (
                      <Flex justify="center" align="center" gap={2}>
                        <Button size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} isDisabled={page === 1}>
                          قبلی
                        </Button>
                        <Text fontSize="sm" px={2}>صفحه {page} از {pages}</Text>
                        <Button size="sm" onClick={() => setPage((p) => Math.min(pages, p + 1))} isDisabled={page === pages}>
                          بعدی
                        </Button>
                      </Flex>
                    )}
                  </>
                )}
              </Flex>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <Text fontSize="md" color={textColor} fontWeight="bold">
                سابقهٔ اعمال فایل‌ها
              </Text>
            </CardHeader>
            <CardBody>
              <Box overflowX="auto" w="100%">
                <Table variant="simple" size="sm" color={textColor}>
                  <Thead>
                    <Tr>
                      <Th borderColor={borderColor}>تاریخ</Th>
                      <Th borderColor={borderColor}>فایل</Th>
                      <Th borderColor={borderColor}>وضعیت</Th>
                      <Th borderColor={borderColor}>تغییرات</Th>
                      <Th borderColor={borderColor}>اعمال‌شده</Th>
                      <Th borderColor={borderColor}>ناموفق</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {imports.length === 0 ? (
                      <Tr>
                        <Td colSpan={6} textAlign="center" py={6}>
                          <Text color="gray.400">هنوز فایلی اعمال نشده است</Text>
                        </Td>
                      </Tr>
                    ) : (
                      imports.map((imp) => (
                        <Tr key={imp._id}>
                          <Td borderColor={borderColor}><Text fontSize="sm">{formatDate(imp.createdAt)}</Text></Td>
                          <Td borderColor={borderColor}><Text fontSize="sm">{imp.fileName || "—"}</Text></Td>
                          <Td borderColor={borderColor}>
                            <Badge colorScheme={IMPORT_STATUS[imp.status]?.color}>
                              {IMPORT_STATUS[imp.status]?.label || imp.status}
                            </Badge>
                          </Td>
                          <Td borderColor={borderColor}>{imp.summary?.changes ?? 0}</Td>
                          <Td borderColor={borderColor}>{imp.summary?.applied ?? 0}</Td>
                          <Td borderColor={borderColor}>{imp.summary?.failed ?? 0}</Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
            </CardBody>
          </Card>
        </Box>
      </Flex>

      <CustomModal
        isOpen={confirm.isOpen}
        onClose={confirm.onClose}
        title="اعمال موجودی فایل انبار"
        size="md"
        onSubmit={handleApply}
        isLoading={isApplying}
        submitText="بله، اعمال کن"
      >
        {diff && (
          <Text fontSize="sm">
            موجودی {diff.changes.length.toLocaleString("fa-IR")} تنوع ({diff.stats.increase.toLocaleString("fa-IR")} افزایش،{" "}
            {diff.stats.decrease.toLocaleString("fa-IR")} کاهش) مطابق فایل «{fileName}» تنظیم و به همهٔ درگاه‌ها ارسال می‌شود.
            اگر فایل قدیمی باشد، فروش‌های بعد از تاریخ فایل نادیده گرفته می‌شوند. ادامه می‌دهید؟
          </Text>
        )}
      </CustomModal>
    </>
  );
}

export default WarehouseStock;
